import { Component, Input, Output, EventEmitter, forwardRef, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  ReactiveFormsModule,
} from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { NiIconComponent } from '../ni-icon/ni-icon.component';
import { generateSecureRandomString } from '../../../../shared/utils/random-utils';

@Component({
  selector: 'NiFormTextField',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NiIconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NiTextFieldComponent),
      multi: true,
    },
  ],
  templateUrl: './ni-text-field.component.html',
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      input[type='number']::-webkit-outer-spin-button,
      input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type='number'] {
        -moz-appearance: textfield;
      }
      .skeleton-shimmer {
        position: relative;
        overflow: hidden;
      }
      .skeleton-shimmer::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.8) 50%,
          transparent 100%
        );
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
      }
      @keyframes shimmer {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    `,
  ],
  animations: [
    trigger('skeletonFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
    trigger('inputFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
  ],
})
export class NiTextFieldComponent implements ControlValueAccessor {
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  
  // Uses cryptographically secure random values for default DOM element ID
  @Input() id: string = `textfield-${generateSecureRandomString(7)}`;
  @Input() name: string = 'textfield';
  @Input() label: string = 'Textfield';
  @Input() placeholder: string = '';
  @Input() type: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' = 'text';
  @Input() value: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() error: string = 'error';
  @Input() showError: boolean = false;
  @Input() helperText: string = '';
  @Input() description: string = '';
  @Input() maxLength?: number;
  @Input() maxDigits?: number;
  @Input() maxDecimalPlaces?: number;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() variant: 'outlined' | 'filled' | 'underlined' = 'outlined';
  @Input() color:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'warning'
    | 'basic'
    | 'link'
    | 'text'
    | 'secondaryLogin' = 'secondary';
  @Input() leftIcon: string = '';
  @Input() rightIcon: string = '';
  @Input() clearable: boolean = true;
  @Input() rounded: boolean = false;
  @Input() rightIconClass: string = '';
  @Input() fullWidth: boolean = true;
  @Input() leftIconDisabled: boolean = false;
  @Input() rightIconDisabled: boolean = false;
  @Input() isOtpField: boolean = false;
  @Input() allowHyphen: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() isPhoneField: boolean = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() focus = new EventEmitter<FocusEvent>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() iconClick = new EventEmitter<string>();
  @Output() onHelperTextClick = new EventEmitter<void>();
  @Input() timerOver = new EventEmitter<any>();

  focused: boolean = false;
  countdownMinutes: number = 0;
  countdownSeconds: number = 0;
  timerInterval: any = null;

  otpTimerKey = 'otp_timer_start_time';
  otpDurationMs = 10 * 60 * 1000;
  
  // Autocomplete prevention - use cryptographically secure random values
  // Uses crypto.getRandomValues() to satisfy security scanner requirements
  private readonly autocompleteValue: string = 'nope-' + generateSecureRandomString(13);
  private readonly autocompleteSafeName: string = 'field-' + generateSecureRandomString(7);

  // ControlValueAccessor implementation
  onChange: any = () => {};
  onTouched: any = () => {};


  ngOnChanges(changes: SimpleChanges) {
    if (changes['rightIconDisabled']) {
      if (this.rightIconDisabled && this.isOtpField) {
        this.startOtpTimer();
      }
    }
  }

  writeValue(value: string): void {
    this.value = value ?? '';
    // Keep the native input in sync — [value] binding can miss updates while disabled/hidden.
    queueMicrotask(() => {
      if (this.inputElement?.nativeElement && this.inputElement.nativeElement.value !== this.value) {
        this.inputElement.nativeElement.value = this.value;
      }
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    let value = target.value;
    
    // Handle SSN formatting (xxx-xx-xxxx)
    if (this.type === 'number' && this.allowHyphen) {
      // Remove all non-digit characters
      const digits = value.replace(/\D/g, '');
      
      // Limit to 9 digits
      const limitedDigits = digits.slice(0, 9);
      
      // Format as xxx-xx-xxxx
      value = this.formatSSN(limitedDigits);
      target.value = value;
    } else if (this.type === 'number' && this.maxDigits) {
      const digits = value.replace(/\D/g, '');
      if (digits.length > this.maxDigits) {
        value = value.slice(0, -1);
        target.value = value;
      }
    }
    
    // Handle decimal places restriction
    if (this.type === 'number' && this.maxDecimalPlaces !== undefined && value !== '') {
      const decimalIndex = value.indexOf('.');
      if (decimalIndex !== -1) {
        const integerPart = value.substring(0, decimalIndex);
        const decimalPart = value.substring(decimalIndex + 1);
        if (decimalPart.length > this.maxDecimalPlaces) {
          value = integerPart + '.' + decimalPart.substring(0, this.maxDecimalPlaces);
          target.value = value;
        }
      }
    }

    // Only notify form when value actually changed (e.g. avoid extra validators/API when at max length)
    const valueChanged = this.value !== value;
    this.value = value;
    if (valueChanged) {
      this.onChange(this.value);
      this.valueChange.emit(this.value);
    }
  }

  onFocus(event?: FocusEvent): void {
    this.focused = true;
    // Remove readonly attribute when focused to allow typing
    if (this.shouldPreventAutocomplete() && this.inputElement?.nativeElement) {
      this.inputElement.nativeElement.removeAttribute('readonly');
    }
    if (event) {
      this.focus.emit(event);
    }
  }

  onBlur(event?: FocusEvent): void {
    this.focused = false;
    this.onTouched();
    if (event) {
      this.blur.emit(event);
    }
  }

  onWheel(event: WheelEvent): void {
    if (this.type === 'number') {
      event.preventDefault();
    }
  }

  clearInput(): void {
    this.value = '';
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  getInputClasses(): string {
    const colorClasses = this.getColorClasses();
    const baseClasses =
      'block w-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-0 focus:border-gray-500';

    const sizeClasses = this.getSizeClasses();
    const paddingClasses = this.getPaddingClasses();
    const { borderClasses, bgClasses } = this.getVariantClasses(colorClasses);
    const roundedClasses = this.rounded ? 'rounded-full' : 'rounded-lg';
    // Disabled classes should override background classes - same text color but bold
    const disabledClasses = this.disabled ? '!bg-gray-200 cursor-not-allowed font-bold' : '';

    return `${baseClasses} ${sizeClasses} ${paddingClasses} ${borderClasses} ${bgClasses} ${roundedClasses} ${disabledClasses}`;
  }

  private getSizeClasses(): string {
    const sizeMap: Record<string, string> = {
      sm: 'py-1.5 text-sm',
      md: 'py-2 text-base',
      lg: 'py-2.5 text-lg',
    };
    return sizeMap[this.size] ?? sizeMap['md'];
  }

  private getPaddingClasses(): string {
    let paddingClasses = '';
    if (this.leftIcon && this.rightIcon) {
      paddingClasses = 'pl-10 pr-10';
    } else if (this.leftIcon) {
      paddingClasses = 'pl-10 pr-3';
    } else if (this.rightIcon || this.clearable) {
      paddingClasses = 'pl-3 pr-10';
    } else {
      paddingClasses = 'px-3';
    }

    // Adjust padding for underlined variant
    if (this.variant === 'underlined') {
      paddingClasses = paddingClasses.replace('px-3', 'px-0');
    }

    return paddingClasses;
  }

  private getVariantClasses(colorClasses: any): { borderClasses: string; bgClasses: string } {
    const hasError = this.showError && this.error;

    if (this.variant === 'outlined') {
      return {
        borderClasses: hasError ? 'border border-danger-500 border-[1px]' : colorClasses.default.border,
        bgClasses: colorClasses.default.bg,
      };
    }

    if (this.variant === 'filled') {
      return {
        borderClasses: hasError
          ? 'border border-t-transparent border-l-transparent border-r-transparent border-b-[2px] border-danger-500'
          : 'border border-t-transparent border-l-transparent border-r-transparent border-b-2 border-gray-300',
        bgClasses: hasError ? 'bg-danger-50' : 'bg-gray-100',
      };
    }

    if (this.variant === 'underlined') {
      return {
        borderClasses: hasError ? 'border-b-1 border-danger-500' : 'border-b-1 border-gray-300',
        bgClasses: 'bg-transparent',
      };
    }

    return { borderClasses: '', bgClasses: '' };
  }

  private getColorClasses(): any {
    // Color system matching the button component
    const colorMap = {
      primary: {
        default: { border: 'border-gray-300', bg: 'bg-gray-100' },
        focus: {
          border: 'border-primary-500',
          ring: 'focus:ring-primary-500',
          bg: 'bg-primary-50',
        },
        error: { border: 'border-danger-500 border-[2px]', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' },
      },
      secondary: {
        default: { border: 'border-gray-600 border-[1px]', bg: 'bg-transparent' },
        focus: { border: 'border-gray-500', ring: 'focus:ring-gray-500', bg: 'bg-secondary-50' },
        error: { border: 'border-danger-500 border-[1px]', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' },
      },
      secondaryLogin: {
        default: { border: 'border-secondary-500 border-[2px]', bg: 'bg-transparent' },
        focus: {
          border: 'border-secondary-500 border-[2px]',
          ring: 'focus:ring-secondary-500',
          bg: 'bg-transparent',
        },
        error: { border: 'border-danger-500 border-[2px]', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' },
      },
      success: {
        default: { border: 'border-gray-300', bg: 'bg-gray-100' },
        focus: {
          border: 'border-success-500',
          ring: 'focus:ring-success-500',
          bg: 'bg-success-50',
        },
        error: { border: 'border-danger-500 border-[2px]', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' },
      },
      error: {
        default: { border: 'border-danger-300 border-[2px]', bg: 'bg-danger-50' },
        focus: { border: 'border-danger-500 border-[2px]', ring: 'focus:ring-danger-500', bg: 'bg-danger-100' },
        error: { border: 'border-danger-500 border-[2px]', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' },
      },
      warning: {
        default: { border: 'border-gray-300', bg: 'bg-gray-100' },
        focus: {
          border: 'border-warning-500',
          ring: 'focus:ring-warning-500',
          bg: 'bg-warning-50',
        },
        error: { border: 'border-danger-500 border-[2px]', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' },
      },
      basic: {
        default: { border: 'border-gray-300', bg: 'bg-gray-100' },
        focus: { border: 'border-gray-500', ring: 'focus:ring-gray-500', bg: 'bg-gray-50' },
        error: { border: 'border-danger-500', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' },
      },
      link: {
        default: { border: 'border-transparent', bg: 'bg-transparent' },
        focus: {
          border: 'border-primary-500',
          ring: 'focus:ring-primary-500',
          bg: 'bg-transparent',
        },
        error: { border: 'border-danger-500', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' },
      },
      text: {
        default: { border: 'border-transparent', bg: 'bg-transparent' },
        focus: { border: 'border-gray-500', ring: 'focus:ring-gray-500', bg: 'bg-gray-50' },
        error: { border: 'border-danger-500', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' },
      },
    };

    return colorMap[this.color] || colorMap.primary;
  }

  getLabelClasses(): string {
    const baseClasses = 'block lg:text-[1.125rem] md:text-[1.125rem] sm:text-[1.125rem] xs:text-[1.125rem] text-[1rem]  font-normal font-archivo text-gray-700 leading-[29px] ';
    return `${baseClasses} text-[#444955]`;
  }

  getRequiredClasses(): string {
    return 'text-danger-500 text-[20px] font-archivo mt-1';
  }

  getDescriptionClasses(): string {
    return 'text-xs text-gray-500';
  }

  /**
   * Extract color name from border class (e.g., 'border-primary-500' -> 'primary')
   */
  private getColorNameFromBorderClass(): string {
    const colorClasses = this.getColorClasses();
    const borderClass = colorClasses.default.border;
    const colorMatch = borderClass.match(/border-(\w+)-/);
    return colorMatch ? colorMatch[1] : 'gray';
  }

  getIconClasses(): string {
    const colorName = this.getColorNameFromBorderClass();
    return `text-${colorName}-500`;
  }

  getClearButtonClasses(): string {
    const colorName = this.getColorNameFromBorderClass();
    return `relative z-0 text-${colorName}-400 hover:text-${colorName}-600 mr-[0.75rem] transition-colors duration-200 focus:ring-2 focus:ring-gray-1300 focus:ring-opacity-50 outline-none`;
  }

  getErrorClasses(): string {
    return 'text-[14px] text-danger-500  font-archivo mt-2';
  }

  getHelperClasses(): string {
    return 'text-[14px] font-medium text-danger-500 font-archivo mt-[4px] ml-auto cursor-pointer';
  }

  onIconClick(iconName: string): void {
    this.iconClick.emit(iconName);
  }

  startOtpTimer() {
    let startTime: number;
    startTime = Date.now();
    this.updateTimer(startTime);
    if (this.timerInterval) clearInterval(this.timerInterval);
    this.timerInterval = setInterval(() => {
      this.updateTimer(startTime);
    }, 1000);
  }

  updateTimer(startTime: number) {
    const elapsed = Date.now() - startTime;
    const remaining = this.otpDurationMs - elapsed;

    if (remaining <= 0) {
      this.countdownMinutes = 0;
      this.countdownSeconds = 0;

      clearInterval(this.timerInterval);
      this.rightIconDisabled = false;
      this.timerOver.emit();
      return;
    }

    this.countdownMinutes = Math.floor(remaining / 1000 / 60);
    this.countdownSeconds = Math.floor((remaining / 1000) % 60);
  }

  onHelperClick() {
    this.onHelperTextClick.emit();
  }

  onKeyDown(event: KeyboardEvent): void {
    // Handle phone field - restrict to digits only
    if (this.isPhoneField) {
      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
      // Allow only digits (0-9) and control keys
      if (!allowedKeys.includes(event.key) && !/^[0-9]$/.test(event.key)) {
        event.preventDefault();
      }
      return;
    }
    
    if (this.type === 'number') {
      if (this.allowHyphen) {
        // Allow only numbers, hyphen, and control keys
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
        if (!allowedKeys.includes(event.key) && !/^[0-9-]$/.test(event.key)) {
          event.preventDefault();
        }
      } else if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-') {
        event.preventDefault();
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    // Handle phone field paste - filter alphabets
    if (this.isPhoneField) {
      const pastedText = event.clipboardData?.getData('text') ?? '';
      // Remove all non-digit characters
      const digits = pastedText.replace(/[^0-9]/g, '').slice(0, 10);
      
      if (digits.length === 0) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      // Format phone number (this will be handled by the parent component's handlePhoneInput)
      this.updateInputValue(digits);
      return;
    }
    
    if (this.type !== 'number') {
      return;
    }

    if (this.allowHyphen) {
      this.handleHyphenPaste(event);
    } else {
      this.handleNumberPaste(event);
    }
  }

  private handleHyphenPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const digits = pastedText.replace(/\D/g, '').slice(0, 9);
    
    if (digits.length === 0) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const formattedValue = this.formatSSN(digits);
    this.updateInputValue(formattedValue);
  }

  private formatSSN(digits: string): string {
    if (digits.length <= 3) {
      return digits;
    }
    if (digits.length <= 5) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }

  private updateInputValue(value: string): void {
    if (!this.inputElement?.nativeElement) {
      return;
    }
    
    this.inputElement.nativeElement.value = value;
    this.value = value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  private handleNumberPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const pattern = /[eE+-]/;
    if (pattern.test(pastedText)) {
      event.preventDefault();
    }
  }

  focusInput(): void {
    this.inputElement?.nativeElement?.focus();
  }

  getInputType(): string {
    return this.type === 'number' && this.allowHyphen ? 'text' : this.type;
  }

  getInputMode(): string | null {
    return this.type === 'number' && this.allowHyphen ? 'numeric' : null;
  }

  isEmailField(): boolean {
    return this.type === 'email' || this.label.toLowerCase().includes('email') || this.placeholder.toLowerCase().includes('email');
  }

  shouldPreventAutocomplete(): boolean {
    // Apply autocomplete prevention to all text, number, tel, and email fields
    // Password fields are excluded as they may need browser password managers
    return this.type === 'text' || 
           this.type === 'email' || 
           this.type === 'number' || 
           this.type === 'tel' || 
           this.type === 'url';
  }

  getAutocompleteValue(): string {
    // Use a random value that browsers won't recognize to prevent autocomplete
    return this.autocompleteValue;
  }

  getAutocompleteSafeName(): string {
    // Modify the name attribute to prevent browser autocomplete
    // Browsers use name/id attributes to identify fields for autocomplete
    // Apply to all fields that should prevent autocomplete
    if (this.shouldPreventAutocomplete()) {
      return this.autocompleteSafeName;
    }
    return this.name;
  }

}
